import React, { useState, useEffect } from 'react';
import { Table, Button, Typography, Space, Tag, Modal, Switch, message, Spin, Descriptions, Card } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { XMLMappingService, XMLMapping, FieldMappings } from '../services/xmlMappingService';
import XmlMappingWizard from '../components/XmlMappingWizard';
import { getActiveBrandId } from '../config/api';

const { Title, Text } = Typography;

interface XmlMappingsProps {
  isComponent?: boolean;
}

const XmlMappings: React.FC<XmlMappingsProps> = ({ isComponent = false }) => {
  const { user } = useAuth();
  const [mappings, setMappings] = useState<XMLMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<XMLMapping | null>(null);
  const [wizardVisible, setWizardVisible] = useState(false);

  // Get active brand ID based on environment (staging uses stageBrandId, prod uses brandId)
  const activeBrandId = getActiveBrandId(user);

  useEffect(() => {
    if (activeBrandId) {
      loadMappings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrandId]);

  const loadMappings = async () => {
    if (!activeBrandId) {
      message.warning('Brand ID bulunamadı');
      return;
    }

    try {
      setLoading(true);
      const response = await XMLMappingService.getXMLMappings({
        brandId: activeBrandId,
        limit: 50,
      });

      if (response.success) {
        setMappings(response.data.mappings);
        setTotal(response.data.pagination.total);
      } else {
        message.error('Mapping\'ler yüklenemedi');
      }
    } catch (error) {
      console.error('Failed to load mappings:', error);
      message.error('Mapping\'ler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (mapping: XMLMapping) => {
    setSelectedMapping(mapping);
    setDetailModalVisible(true);
  };

  const handleToggleActive = async (mappingId: string, currentStatus: boolean) => {
    try {
      const success = await XMLMappingService.toggleXMLMapping(mappingId, !currentStatus);
      if (success) {
        message.success('Mapping durumu güncellendi');
        loadMappings();
      } else {
        message.error('Güncelleme başarısız');
      }
    } catch (error) {
      message.error('Hata oluştu');
    }
  };

  const handleDelete = async (mappingId: string) => {
    try {
      const success = await XMLMappingService.deleteXMLMapping(mappingId);
      if (success) {
        message.success('Mapping silindi');
        loadMappings();
      } else {
        message.error('Silme başarısız');
      }
    } catch (error) {
      message.error('Hata oluştu');
    }
  };

  const renderFieldMappings = (fieldMappings: FieldMappings) => {
    return (
      <div style={{ maxHeight: 400, overflow: 'auto' }}>
        {Object.entries(fieldMappings).map(([key, values]) => (
          <div key={key} style={{ marginBottom: 8 }}>
            <Text strong>{key}: </Text>
            <Space wrap>
              {values?.map((value, index) => (
                <Tag key={index} color="blue">
                  {value}
                </Tag>
              ))}
            </Space>
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title: 'Mapping Adı',
      dataIndex: 'mappingName',
      key: 'mappingName',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Field Count',
      dataIndex: 'fieldMappings',
      key: 'fieldCount',
      render: (fieldMappings: FieldMappings) => (
        <Tag color="purple">{Object.keys(fieldMappings).length} alan</Tag>
      ),
    },
    {
      title: 'XPath Config',
      dataIndex: 'xpathConfig',
      key: 'xpathConfig',
      render: (config: any) => (
        config ? <Tag color="green">Yapılandırılmış</Tag> : <Tag>Yok</Tag>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: XMLMapping) => (
        <Space>
          {isActive ? (
            <Tag icon={<CheckCircleOutlined />} color="success">Aktif</Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="error">Pasif</Tag>
          )}
          <Switch
            size="small"
            checked={isActive}
            onChange={() => handleToggleActive(record.id, isActive)}
          />
        </Space>
      ),
    },
    {
      title: 'Oluşturulma',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: XMLMapping) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            Detay
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Mapping\'i silmek istediğinizden emin misiniz?',
                content: `${record.mappingName} silinecek`,
                okText: 'Evet',
                cancelText: 'Hayır',
                onOk: () => handleDelete(record.id),
              });
            }}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  if (!activeBrandId) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Brand ID bekleniyor...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: isComponent ? 'flex-end' : 'space-between',
          alignItems: 'center',
        }}
      >
        {!isComponent && (
          <div>
            <Title level={2}>XML Mapping</Title>
            <Text type="secondary">XML Field eşleştirmelerinizi görüntüleyin ({total} mapping)</Text>
          </div>
        )}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setWizardVisible(true)}
        >
          Yeni Mapping
        </Button>
      </div>

      <Table 
        columns={columns} 
        dataSource={mappings} 
        rowKey="id" 
        loading={loading}
        pagination={false} 
      />

      {/* Detail Modal */}
      <Modal
        title="Mapping Detayları"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedMapping(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Kapat
          </Button>,
        ]}
        width={800}
      >
        {selectedMapping && (
          <div>
            <Descriptions column={1} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Mapping Adı">
                <Text strong>{selectedMapping.mappingName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Brand ID">
                <Text code>{selectedMapping.brandId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                {selectedMapping.isActive ? (
                  <Tag icon={<CheckCircleOutlined />} color="success">Aktif</Tag>
                ) : (
                  <Tag icon={<CloseCircleOutlined />} color="error">Pasif</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Oluşturulma">
                {new Date(selectedMapping.createdAt).toLocaleString('tr-TR')}
              </Descriptions.Item>
              {selectedMapping.updatedAt && (
                <Descriptions.Item label="Son Güncelleme">
                  {new Date(selectedMapping.updatedAt).toLocaleString('tr-TR')}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Card title="Field Mappings" size="small" style={{ marginBottom: 16 }}>
              {renderFieldMappings(selectedMapping.fieldMappings)}
            </Card>

            {selectedMapping.xpathConfig && (
              <Card title="XPath Configuration" size="small">
                <Descriptions column={1} size="small">
                  {Object.entries(selectedMapping.xpathConfig).map(([key, value]) => (
                    <Descriptions.Item key={key} label={key}>
                      <Text code>{value}</Text>
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </Card>
            )}

            {selectedMapping.brand && (
              <Card title="Brand Bilgisi" size="small" style={{ marginTop: 16 }}>
                <Space direction="vertical">
                  <Text><strong>Ad:</strong> {selectedMapping.brand.name}</Text>
                  {selectedMapping.brand.websiteUrl && (
                    <Text>
                      <strong>Website:</strong>{' '}
                      <a href={selectedMapping.brand.websiteUrl} target="_blank" rel="noopener noreferrer">
                        {selectedMapping.brand.websiteUrl}
                      </a>
                    </Text>
                  )}
                  {selectedMapping.brand.logoUrl && (
                    <div>
                      <Text strong>Logo:</Text>
                      <div style={{ marginTop: 8 }}>
                        <img 
                          src={selectedMapping.brand.logoUrl} 
                          alt={selectedMapping.brand.name}
                          style={{ maxHeight: 60 }}
                        />
                      </div>
                    </div>
                  )}
                </Space>
              </Card>
            )}
          </div>
        )}
      </Modal>

      {/* Wizard Modal */}
      {activeBrandId && (
        <XmlMappingWizard
          visible={wizardVisible}
          brandId={activeBrandId}
          onClose={() => setWizardVisible(false)}
          onSuccess={() => {
            loadMappings();
          }}
        />
      )}
    </div>
  );
};

export default XmlMappings;

